import React, { useCallback, useEffect, useState } from 'react'
import {
  Box, Button, Chip, CircularProgress, Divider, Stack, TextField, Typography,
} from '@mui/material'
import moment from 'moment'
import { toast } from 'react-toastify'
import { messagingService, type ApiMessage, type ApiThread } from '../../services/messaging.service'

/**
 * Patient messages, as a third tab beside Tasks and Activity.
 *
 * WHY NOT ROWS IN THE TASK LIST. That list is already dense with required
 * clinical actions, and a message dropped into it either gets lost or displaces
 * a med-evaluation task. A message is a different KIND of obligation — it is
 * read and answered in prose, not checked off. It still CREATES a task
 * (adhoc_origin=PATIENT_TRIGGERED, ref_table='message_thread') so that SLA
 * breaches surface in the worklist and reporting the practice already runs; the
 * task is the bookkeeping, this tab is the reading surface.
 *
 * ORDERED BY WHO HAS WAITED LONGEST — the server sorts on awaiting_reply_since,
 * not recency. This tab answers "who is owed a reply", and newest-first buries
 * the oldest unanswered question under the most recent chatter.
 *
 * THE SLA IS A PROMISE, NOT A GATE. A breach is shown so it can be worked; it
 * never withholds, closes or deprioritises anything.
 */

interface Props {
  patientRefId: string
}

/** Badge count for the tab label. Lives here so the parent's edit stays a
 *  single line — TabPanel unmounts its children when inactive, so the count
 *  cannot come from the panel itself. */
export function useAwaitingMessageCount(patientRefId: string) {
  const [count, setCount] = useState(0)
  useEffect(() => {
    let alive = true
    if (!patientRefId) return
    messagingService.getThreads(patientRefId)
      .then((r) => { if (alive) setCount(r.awaiting_count) })
      .catch(() => { /* a badge must never surface an error toast */ })
    return () => { alive = false }
  }, [patientRefId])
  return count
}

function when(ts?: number | null) {
  return ts ? moment.unix(ts).format('MMM D, h:mm a') : ''
}

/** Who signed a message (PORTAL_MESSAGING_ADR D14). We show the ACTUAL author +
 *  their role, so a navigator relaying logistics ("Dr. Deo is out, I can route
 *  you") reads as the navigator and never masquerades as the cardiologist. Falls
 *  back to a generic label only for an older, unsigned provider message. */
function signature(m: ApiMessage): string {
  if (m.author_kind === 'patient') return 'Patient'
  const roleLabel = m.author_role === 'navigator' ? 'Care Team'
    : m.author_role === 'provider' ? 'Cardiologist'
    : 'Care Team'
  return m.author_name ? `${m.author_name} · ${roleLabel}` : 'Care team'
}

const PatientMessagesComponent: React.FC<Props> = ({ patientRefId }) => {
  const [threads, setThreads] = useState<ApiThread[]>([])
  const [slaHours, setSlaHours] = useState<number | null>(null)
  const [open, setOpen] = useState<ApiThread | null>(null)
  const [draft, setDraft] = useState('')
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!patientRefId) return
    setLoading(true)
    try {
      const res = await messagingService.getThreads(patientRefId)
      setThreads(res.threads)
      setSlaHours(res.sla_hours)
      setError(null)
    } catch (e: any) {
      // Never render a failed load as "no messages" — that reads as "this
      // patient has not written", which is the opposite of the truth.
      setError(e?.response?.data?.error?.message || 'Could not load messages.')
    } finally {
      setLoading(false)
    }
  }, [patientRefId])

  useEffect(() => { void refresh() }, [refresh])

  const openThread = async (id: number) => {
    try {
      setOpen(await messagingService.getThread(patientRefId, id))
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Could not open that thread.')
    }
  }

  const send = async (close: boolean) => {
    const body = draft.trim()
    if (!body || !open || sending) return
    setSending(true)
    try {
      setOpen(await messagingService.reply(patientRefId, open.id, body, close))
      setDraft('')
      await refresh()
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Could not send the reply.')
    } finally {
      setSending(false)
    }
  }

  if (error) {
    return <Box p={1.5}><Typography variant="body2" color="error">{error}</Typography></Box>
  }
  if (loading && threads.length === 0) {
    return <Box p={2} textAlign="center"><CircularProgress size={18} /></Box>
  }

  if (open) {
    return (
      <Box p={1.25}>
        <Button size="small" onClick={() => setOpen(null)}
                sx={{ textTransform: 'none', pl: 0, minWidth: 0 }}>
          ← All messages
        </Button>
        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', mt: 0.5 }}>
          {open.display_label}
        </Typography>
        <SlaLine thread={open} />
        <Divider sx={{ my: 1 }} />

        <Stack spacing={1}>
          {(open.messages || []).map((m) => <Bubble key={m.id} m={m} />)}
        </Stack>

        <TextField fullWidth multiline minRows={3} size="small" value={draft}
                   placeholder="Reply to the patient"
                   onChange={(e) => setDraft(e.target.value)} sx={{ mt: 1.5 }} />
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button variant="contained" size="small" disabled={sending || !draft.trim()}
                  onClick={() => void send(false)} sx={{ textTransform: 'none' }}>
            Send
          </Button>
          <Button variant="outlined" size="small" disabled={sending || !draft.trim()}
                  onClick={() => void send(true)} sx={{ textTransform: 'none' }}>
            Send &amp; close
          </Button>
        </Stack>
      </Box>
    )
  }

  return (
    <Box p={1.25}>
      {slaHours != null && (
        <Typography sx={{ fontSize: '0.68rem', color: 'var(--color-text-secondary)', mb: 0.75 }}>
          Promised turnaround for this patient: {slaHours}h
        </Typography>
      )}
      {threads.length === 0 && (
        <Typography sx={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
          No messages from this patient.
        </Typography>
      )}
      {threads.map((th) => (
        <Box key={th.id} onClick={() => void openThread(th.id)}
             sx={{ py: 1, borderBottom: '1px solid var(--color-border)', cursor: 'pointer',
                   '&:hover': { backgroundColor: 'var(--color-background-subtle)' } }}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: th.unread_count > 0 ? 700 : 500 }}>
              {th.display_label}
            </Typography>
            {th.sla_breached && (
              <Chip size="small" label="overdue" color="error"
                    sx={{ height: 16, fontSize: '0.6rem' }} />
            )}
            <Box flex={1} />
            <Typography sx={{ fontSize: '0.65rem', color: 'var(--color-text-secondary)' }}>
              {when(th.last_message_on)}
            </Typography>
          </Stack>
          {th.preview && (
            <Typography sx={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)',
                              overflow: 'hidden', textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap' }}>
              {th.preview_author === 'provider' ? 'You: ' : ''}{th.preview}
            </Typography>
          )}
          <SlaLine thread={th} />
        </Box>
      ))}
    </Box>
  )
}

function SlaLine({ thread }: { thread: ApiThread }) {
  if (!thread.awaiting_reply_since) {
    return (
      <Typography sx={{ fontSize: '0.65rem', color: 'var(--color-text-secondary)' }}>
        Answered
      </Typography>
    )
  }
  const waiting = moment.unix(thread.awaiting_reply_since).fromNow(true)
  return (
    <Typography sx={{ fontSize: '0.65rem',
                      color: thread.sla_breached ? 'var(--color-error, #d32f2f)'
                                                 : 'var(--color-text-secondary)' }}>
      Waiting {waiting} · due {when(thread.sla_due_on)}
    </Typography>
  )
}

function Bubble({ m }: { m: ApiMessage }) {
  const fromPatient = m.author_kind === 'patient'
  return (
    <Box sx={{ alignSelf: fromPatient ? 'flex-start' : 'flex-end', maxWidth: '88%' }}>
      <Box sx={{ px: 1.25, py: 0.9, borderRadius: 1,
                 border: '1px solid var(--color-border)',
                 backgroundColor: fromPatient
                   ? 'var(--color-background-subtle)' : 'transparent' }}>
        <Typography sx={{ fontSize: '0.76rem', whiteSpace: 'pre-wrap' }}>{m.body}</Typography>
      </Box>
      <Typography sx={{ fontSize: '0.6rem', color: 'var(--color-text-secondary)', mt: 0.25 }}>
        {signature(m)} · {when(m.created_on)}
      </Typography>
    </Box>
  )
}

export default PatientMessagesComponent
